import {
  faBinoculars,
  faBookOpen,
  faBuilding,
  faClipboardCheck,
  faEye,
  faKey,
  faPencil,
  faPenToSquare,
  faRocket,
  faShieldAlt,
  faUser,
  faUserCheck,
  faUserGear,
  faUserPlus,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";

export type RoleScope = "platform" | "organization" | "workspace";

export type RoleDefinition = {
  name: string;
  displayName: string;
  description: string;
  icon: IconDefinition;
  tileClassName: string;
  scope: RoleScope;
};

export const ROLE_CATALOG: RoleDefinition[] = [
  {
    name: "taktile_d_admin",
    displayName: "Taktile Admin",
    description: "Administrative access to the Taktile platform",
    icon: faShieldAlt,
    tileClassName: "border-indigo-200 bg-indigo-50",
    scope: "platform",
  },
  {
    name: "oncall_group",
    displayName: "On-Call Group",
    description:
      "Read-only access to all customer resources, can escalate to on-call write",
    icon: faEye,
    tileClassName: "border-orange-200 bg-orange-50",
    scope: "platform",
  },
  {
    name: "oncall_write",
    displayName: "On-Call Write",
    description: "Read and write access to all customer resources",
    icon: faPencil,
    tileClassName: "border-red-200 bg-red-50",
    scope: "platform",
  },
  {
    name: "access_requester",
    displayName: "Access Requester",
    description: "Can request access to organizations across the platform",
    icon: faUserPlus,
    tileClassName: "border-sky-200 bg-sky-50",
    scope: "platform",
  },
  {
    name: "access_approver",
    displayName: "Access Approver",
    description: "Can review and approve or reject access requests",
    icon: faUserCheck,
    tileClassName: "border-green-200 bg-green-50",
    scope: "platform",
  },
  {
    name: "fwd_d",
    displayName: "Forward Deployed Admin",
    description: "Forward deployment capabilities in the assigned organization",
    icon: faRocket,
    tileClassName: "border-neo-green-200 bg-neo-green-50",
    scope: "organization",
  },
  {
    name: "fwd_viewer",
    displayName: "Forward Deployed Viewer",
    description: "Read-only access to the assigned organization",
    icon: faBinoculars,
    tileClassName: "border-pink-200 bg-pink-50",
    scope: "organization",
  },
  {
    name: "d_owner",
    displayName: "Organization Owner",
    description:
      "Full control of the organization, infrastructure settings and audit logs, plus read access to every workspace",
    icon: faBuilding,
    tileClassName: "border-gray-200 bg-gray-50",
    scope: "organization",
  },
  {
    name: "d_member",
    displayName: "Organization Member",
    description:
      "Basic membership: lets the user sign in and see the organization, grants no workspace access on its own",
    icon: faUser,
    tileClassName: "border-gray-200 bg-gray-50",
    scope: "organization",
  },
  {
    name: "ws_member",
    displayName: "Workspace Member",
    description:
      "Basic membership: lets the user open the workspace, grants no access to its contents on its own",
    icon: faUser,
    tileClassName: "border-gray-200 bg-gray-50",
    scope: "workspace",
  },
  {
    name: "ws_viewer",
    displayName: "Workspace Viewer",
    description:
      "Read-only: browse flows, data model, connections and agents, cannot change or run anything",
    icon: faBookOpen,
    tileClassName: "border-gray-200 bg-gray-50",
    scope: "workspace",
  },
  {
    name: "ws_reviewer",
    displayName: "Workspace Reviewer",
    description:
      "Manual review work: open cases, submit review decisions, upload files, cannot see or edit flows",
    icon: faClipboardCheck,
    tileClassName: "border-gray-200 bg-gray-50",
    scope: "workspace",
  },
  {
    name: "ws_editor",
    displayName: "Workspace Editor",
    description:
      "Build and test: everything from viewer and reviewer, plus create and edit flows, data, agents and cases, and run in sandbox, no publishing to production",
    icon: faPenToSquare,
    tileClassName: "border-gray-200 bg-gray-50",
    scope: "workspace",
  },
  {
    name: "ws_admin",
    displayName: "Workspace Admin",
    description:
      "Full control: everything from editor, plus publish to production, connections and secrets, API keys and decision history",
    icon: faUserGear,
    tileClassName: "border-gray-200 bg-gray-50",
    scope: "workspace",
  },
  {
    name: "ws_manager",
    displayName: "Workspace Manager",
    description:
      "Workspace administration: manage users and permissions, API keys and folders, plus read access to every folder",
    icon: faKey,
    tileClassName: "border-gray-200 bg-gray-50",
    scope: "workspace",
  },
];

export const PLATFORM_ROLES = ROLE_CATALOG.filter(
  (role) => role.scope === "platform",
);

const MANAGED_ROLE_NAMES = [
  "taktile_d_admin",
  "oncall_group",
  "oncall_write",
  "fwd_viewer",
  "fwd_d",
  "access_requester",
  "access_approver",
];

export const MANAGED_ROLES: RoleDefinition[] = MANAGED_ROLE_NAMES.flatMap(
  (name) => {
    const definition = ROLE_CATALOG.find((role) => role.name === name);
    return definition ? [definition] : [];
  },
);

export const getRoleDefinition = (name: string): RoleDefinition | undefined =>
  ROLE_CATALOG.find((role) => role.name === name);

export const getRoleDisplayName = (name: string): string =>
  getRoleDefinition(name)?.displayName ?? name;

const SCOPE_ORDER: Record<RoleScope, number> = {
  platform: 0,
  organization: 1,
  workspace: 2,
};

export const compareRoleNames = (a: string, b: string): number => {
  const rank = (name: string) => {
    const scope = getRoleDefinition(name)?.scope;
    return scope === undefined ? 3 : SCOPE_ORDER[scope];
  };
  return (
    rank(a) - rank(b) ||
    getRoleDisplayName(a).localeCompare(getRoleDisplayName(b))
  );
};
